<template>
    <div class="advancedAutoresponder">
        <h2>{{$t('title')}}</h2>
        <p><small>{{$t('info')}}</small></p>
        <b-card :title="$t('definitions')" class="mb-3">
            <b-table :items="localAutoresponders" :fields="autoresponderColumns" selectable
                    select-mode="single" small sticky-header hover
                    :tbody-tr-class="getAutoresponderRowClass"
                    @row-selected="autoresponderSelected"
                    :tbody-transition-props="{name:'flip-list'}"
                    show-empty :empty-text="$t('noAutoresponders')"
                    primary-key="id">
                <template v-slot:cell(text)="data">
                    {{getTextPreview(data.item)}}
                </template>
            </b-table>
            <b-button @click="doAddAutoresponder">{{$t('definitionAdd')}}
                <font-awesome-icon icon="plus"></font-awesome-icon>
            </b-button>
            <b-button :disabled="!isAutoresponderSelected" @click="doEditAutoresponder" class="ml-2">
              {{$t('definitionEdit')}}
                <font-awesome-icon icon="pencil"></font-awesome-icon>
            </b-button>
            <b-button :disabled="!isAutoresponderSelected" @click="doRemoveAutoresponder" class="ml-2">
              {{$t('definitionDelete')}}
                <font-awesome-icon icon="times"></font-awesome-icon>
            </b-button>

            <autoresponder-editor v-model="autoresponderBeingEdited"
                    @hide="autoresponderBeingEdited=null" @input="doSaveAutoresponder"></autoresponder-editor>
        </b-card>

        <b-card :title="$t('rules')" class="mb-3">
            <b-table :items="localRules" ref="ruleTable" :fields="ruleColumns" selectable
                    select-mode="single" small sticky-header hover
                    :tbody-tr-class="getRuleRowClass"
                    :tbody-transition-props="{name:'flip-list'}"
                    show-empty :empty-text="$t('noRules')"
                    @row-selected="ruleSelected" primary-key="id">
                <template v-slot:cell(name)="data">
                    {{data.value}}
                    <font-awesome-icon icon="lock" v-if="data.item.locked"></font-awesome-icon>
                </template>
                <template v-slot:cell(state)="data">
                    {{$t(`ruleStates.${ruleStates[data.item.id]}`)}}
                    <span v-if="data.item.locked">{{$t('ruleStates.locked')}}</span>
                </template>
                <template v-slot:cell(autoresponder)="data">
                    {{getRuleAutoresponderName(data.item)}}
                </template>
            </b-table>

            <b-button @click="doAddRule"
                    :disabled="this.localAutoresponders.length + this.globalAutoresponders.length === 0">
                {{$t('ruleAdd')}}
                <font-awesome-icon icon="plus"></font-awesome-icon>
            </b-button>
            <b-button @click="doEditRule" :disabled="!isSelectedRuleEditable" class="ml-2">{{$t('ruleEdit')}}
                <font-awesome-icon icon="pencil"></font-awesome-icon>
            </b-button>
            <b-button :disabled="!isSelectedRuleEditable" @click="doRemoveRule" class="ml-2">{{$t('ruleDelete')}}
                <font-awesome-icon icon="times"></font-awesome-icon>
            </b-button>
            <b-button :disabled="!canMoveSelectedRuleUp" @click="doMoveRuleUp" class="ml-2">{{$t('ruleMoveUp')}}
                <font-awesome-icon icon="chevron-up"></font-awesome-icon>
            </b-button>
            <b-button :disabled="!canMoveSelectedRuleDown" @click="doMoveRuleDown" class="ml-2">{{$t('ruleMoveDown')}}
                <font-awesome-icon icon="chevron-down"></font-awesome-icon>
            </b-button>
            <autoresponder-rule-editor v-model="ruleBeingEdited"
                    :autoresponders="[...localAutoresponders, ...globalAutoresponders]"
                    @hide="ruleBeingEdited=null" @input="doSaveRule"></autoresponder-rule-editor>
        </b-card>

        <b-button @click="doSave" variant="primary" :disabled="saving">
            {{$t('save')}}
        </b-button>
        <b-button @click="doReset">
            {{$t('reset')}}
        </b-button>

        <font-awesome-icon :class="{ saveIcon: true, savingIcon: saving }" spin
                icon="spinner-third"></font-awesome-icon>
        <save-success-indicator v-model="saveSuccess"></save-success-indicator>
        <span class="error">{{saveError}}</span>
    </div>
</template>

<script>
import { DateTime, FixedOffsetZone } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AutoresponderEditor from '@/components/autoresponderEditor';
import AutoresponderRuleEditor from '@/components/autoresponderRuleEditor';
import axios from 'axios';
import SaveSuccessIndicator from '@/components/saveSuccessIndicator';

export default {
  name: 'advancedAutoresponder',
  components: { SaveSuccessIndicator, FontAwesomeIcon, AutoresponderEditor, AutoresponderRuleEditor },
  i18nOptions: { keyPrefix: 'settings.autoresponders.advanced' },
  data: function() {
    return {
      selectedAutoresponders: [],
      selectedRules: [],
      localAutoresponders: [],
      localRules: [],
      now: null,
      zone: null,
      timeUpdater: null,
      nextAutoresponderId: -1,
      nextRuleId: -1,
      autoresponderBeingEdited: {},
      ruleBeingEdited: {},
      saving: false,
      saveSuccess: false,
      saveError: ''
    };
  },
  props: {
    autoresponders: Array,
    globalAutoresponders: Array,
    rules: Array,
    timezone: Number
  },
  watch: {
    autoresponders(newVal) {
      this.localAutoresponders = newVal.map(a => Object.assign({}, a));
    },
    rules(newVal) {
      this.localRules = newVal.map(r => Object.assign({}, r));
    }
  },
  created() {
    this.localAutoresponders = this.autoresponders.map(a => Object.assign({}, a));
    this.localRules = this.rules.map(r => Object.assign({}, r));
    this.zone = FixedOffsetZone.instance(this.timezone / 60);
    this.now = DateTime.fromObject({ zone: this.zone });
    this.timeUpdater = setInterval(() => {
      this.updateTime();
    }, 10000);
  },
  beforeDestroy() {
    if (this.timeUpdater) {
      clearInterval(this.timeUpdater);
    }
  },
  methods: {
    doSave() {
      this.saving = true;
      this.saveSuccess = false;
      this.saveError = '';
      let autoresponderRequests = [];
      let autoresponderRequestInfo = [];

      this.localAutoresponders.forEach(a => {
        if (a.id > 0) {
          autoresponderRequests.push(axios.put(`/rest/user/settings/autoresponders/${a.id}`, a));
          autoresponderRequestInfo.push({ action: 'update', id: a.id });
        } else {
          autoresponderRequests.push(axios.post('/rest/user/settings/autoresponders',
            Object.assign({}, a, { id: 0 })));
          autoresponderRequestInfo.push({ action: 'create', id: a.id });
        }
      });
      this.autoresponders.forEach(a => {
        if (!this.localAutoresponders.find(la => la.id === a.id)) {
          autoresponderRequests.push(axios.delete(`/rest/user/settings/autoresponders/${a.id}`));
          autoresponderRequestInfo.push({ action: 'delete', id: a.id });
        }
      });
      axios.all(autoresponderRequests)
        .then(d => {
          autoresponderRequestInfo.forEach((info, responseIndex) => {
            if (info.action === 'delete') {
              this.$emit('deleteAutoresponder', { id: info.id });
            } else {
              const localIndex = this.localAutoresponders.findIndex(a => a.id === info.id);
              if (localIndex !== -1) {
                if (this.localAutoresponders[localIndex].id < 0) {
                  autoresponderRequestInfo[responseIndex].newId = d[responseIndex].data.autoresponder.id;
                }
                this.localAutoresponders[localIndex] = d[responseIndex].data.autoresponder;
                console.log('setAutoresponder', d[responseIndex].data.autoresponder);
                this.$emit('setAutoresponder', { autoresponder: d[responseIndex].data.autoresponder });
              }
            }
          });
          console.log('saved autoresponders', autoresponderRequestInfo);
          const saveRules = this.localRules.map(r => {
            let rule = Object.assign({}, r);
            let requestInfo = autoresponderRequestInfo.find(info => info.id === r.autoresponderid);
            if (requestInfo && requestInfo.newId) {
              rule.autoresponderid = requestInfo.newId;
            }
            if (rule.id < 0) {
              rule.id = 0;
            }
            return rule;
          });
          axios.put('/rest/user/settings/autoresponderrules', { rules: saveRules })
            .then(d => {
              this.$emit('setAutoresponderRules', d.data.rules);
              this.saveSuccess = true;
            })
            .catch(e => {
              this.saveError = this.$t('saveAutoresponderRuleError', { error: e.message });
            })
            .finally(() => {
              this.saving = false;
            });
        })
        .catch((e) => {
          this.$dialog.alert(this.$t('saveAutoresponderError'));
          this.saving = false;
        });
    },
    doReset() {
      this.localAutoresponders = this.autoresponders.map(a => Object.assign({}, a));
      this.localRules = this.rules.map(r => Object.assign({}, r));
    },
    updateTime() {
      this.now = DateTime.fromObject({ zone: this.zone });
    },
    doAddAutoresponder() {
      // const newAuto = Object.assign({}, this.localAutoresponders[0], { id: this.nextAutoresponderId-- });
      // this.localAutoresponders.splice(0, 0, newAuto);
      this.autoresponderBeingEdited = {
        appendmsg: 0,
        attachments: [],
        checklog: 168,
        copyto: [],
        id: this.nextAutoresponderId--,
        keep: true,
        name: this.$t('autoresponderNewName', { date: this.now }),
        owner: -1,
        replyto: '',
        respondafter: 0,
        sender: '',
        subject: '',
        subjectprefix: '*',
        text: '',
        to: [ '*' ]
      };
    },
    doEditAutoresponder() {
      this.autoresponderBeingEdited = Object.assign(this.selectedAutoresponders[0]);
    },
    doSaveAutoresponder(autoresponder) {
      console.log('save', autoresponder);
      const ind = this.localAutoresponders.findIndex(a => a.id === autoresponder.id);
      if (ind !== -1) {
        this.localAutoresponders.splice(ind, 1, Object.assign({}, autoresponder));
      } else {
        this.localAutoresponders.push(Object.assign({}, autoresponder));
      }
    },
    doRemoveAutoresponder() {
      const ind = this.localAutoresponders.findIndex(a => a.id === this.selectedAutoresponders[0].id);
      if (ind !== -1) {
        const removed = this.localAutoresponders.splice(ind, 1);
        let ruleInd = -1;
        do {
          ruleInd = this.localRules.findIndex(r => r.autoresponderid === removed[0].id);
          if (ruleInd !== -1) {
            this.localRules.splice(ruleInd, 1);
          }
        } while (ruleInd !== -1);
      }
    },
    doAddRule() {
      let autoresponderid = 0;
      if (this.localAutoresponders.length > 0) {
        autoresponderid = this.localAutoresponders[0].id;
      }
      this.ruleBeingEdited = {
        autoresponderenable: true,
        autoresponderid,
        autoresponderowner: -1,
        customfields: '',
        datefrom: '',
        datetimefrom: '',
        datetimeto: '',
        dateto: '',
        daysofweek: '0123456',
        id: this.nextRuleId--,
        locked: false,
        matches: 0,
        name: this.$t('ruleNewName', { date: this.now }),
        priority: 0,
        ruleenable: true,
        sender: '',
        subject: '',
        timefrom: '',
        timeto: '',
        usernumber: 0
      };
    },
    doEditRule() {
      if (this.isSelectedRuleEditable) {
        this.ruleBeingEdited = Object.assign(this.selectedRules[0]);
      }
    },
    doSaveRule(rule) {
      console.log('save', rule);
      const ind = this.localRules.findIndex(r => r.id === rule.id);
      if (ind !== -1) {
        this.localRules.splice(ind, 1, Object.assign({}, rule));
      } else {
        this.localRules.push(Object.assign({}, rule));
      }
    },
    doRemoveRule() {
      const ind = this.localRules.findIndex(r => r.id === this.selectedRules[0].id);
      if (ind !== -1) {
        this.localRules.splice(ind, 1);
      }
    },
    autoresponderSelected(rows) {
      this.selectedAutoresponders = rows;
    },
    ruleSelected(rows) {
      this.selectedRules = rows;
    },
    getTextPreview(autoresponder) {
      if (autoresponder.text.length > 100) {
        return autoresponder.text.substr(0, 100) + '...';
      } else {
        return autoresponder.text;
      }
    },
    getAutoresponder(id) {
      let a = this.localAutoresponders.find(a => a.id === id);
      if (a) {
        return a;
      }
      a = this.globalAutoresponders.find(a => a.id === id);
      return a;
    },
    getRuleAutoresponderName(rule) {
      const a = this.getAutoresponder(rule.autoresponderid);
      if (a) {
        if (!rule.autoresponderenable) {
          return this.$t('autoresponderNone');
        } else if (a.owner === 0) {
          return this.$t('autoresponderNameGlobal', { name: a.name });
        } else {
          return this.$t('autoresponderNamePersonal', { name: a.name });
        }
      } else {
        return this.$t('autoresponderUnknown');
      }
    },
    getRuleState(rule) {
      if (!rule.ruleenable) {
        return 'disabled';
      }
      let state = 'active';
      const hasTimeConditions = rule.datefrom || rule.dateto || rule.datetimefrom ||
        rule.datetimeto || rule.timefrom || rule.timeto || (rule.daysofweek !== '0123456');
      if (hasTimeConditions) {
        let dateValid = true;
        const now = this.now; // DateTime.fromObject({ zone: Zone });
        if (rule.daysofweek.indexOf(now.weekday % 7) === -1) {
          dateValid = false;
        }
        if (dateValid && rule.datefrom) {
          const df = DateTime.fromSQL(rule.datefrom, { zone: this.zone });
          if (df > now) {
            dateValid = false;
          }
        }
        if (dateValid && rule.dateto) {
          const dt = DateTime.fromSQL(`${rule.dateto} 23:59:59`, { zone: this.zone });
          if (dt < now) {
            dateValid = false;
          }
        }
        if (dateValid && (rule.timefrom || rule.timeto)) {
          const tn = now.toFormat('HH:mm:ss');
          if (rule.timefrom > tn) {
            dateValid = false;
          }
          if (rule.timeto < tn) {
            dateValid = false;
          }
        }
        if (dateValid && rule.datetimefrom) {
          const dtf = DateTime.fromISO(rule.datetimefrom);
          if (dtf > now) {
            dateValid = false;
          }
        }
        if (dateValid && rule.datetimeto) {
          const dtt = DateTime.fromISO(rule.datetimeto);
          if (dtt < now) {
            dateValid = false;
          }
        }

        if (dateValid) {
          state = 'activeTime';
        } else {
          state = 'inactive';
        }
      } else {
        state = 'active';
      }
      if (rule.sender) {
        state += 'Sender';
      }
      if (rule.subject) {
        state += 'Subject';
      }

      return state;
    },
    getAutoresponderRowClass(item, type) {
      if (!item || type !== 'row') {
        return;
      }
      if (this.selectedRules.find(r => r.autoresponderid === item.id)) {
        return 'autoresponderForRule';
      }
    },
    getRuleRowClass(item, type) {
      if (!item || type !== 'row') {
        return;
      }
      if (!item.ruleenable) {
        return 'disabledRule';
      }
    },
    doMoveRuleUp() {
      const ind = this.localRules.findIndex(r => r.id === this.selectedRules[0].id);
      console.log('move rule up ind', ind);
      if (ind > 0) {
        const r = this.localRules.splice(ind, 1);
        this.localRules.splice(ind - 1, 0, ...r);
        this.$nextTick(() => {
          this.$refs.ruleTable.selectRow(ind - 1);
        });
      }
    },
    doMoveRuleDown() {
      const ind = this.localRules.findIndex(r => r.id === this.selectedRules[0].id);
      console.log('move rule down ind', ind);
      if (ind < this.localRules.length - 1) {
        const r = this.localRules.splice(ind, 1);
        this.localRules.splice(ind + 1, 0, ...r);
        this.$nextTick(() => {
          this.$refs.ruleTable.selectRow(ind + 1);
        });
      }
    }
  },
  computed: {
    autoresponderColumns() {
      return [
        { key: 'name', sortable: true, label: this.$t('definitionName') },
        { key: 'text', sortable: true, label: this.$t('definitionText') }
      ];
    },
    ruleColumns() {
      return [
        { key: 'name', label: this.$t('ruleName') },
        { key: 'state', label: this.$t('ruleState') },
        { key: 'autoresponder', label: this.$t('ruleAutoresponder') }
      ];
    },
    ruleStates() {
      return this.localRules.reduce((o, r) => {
        o[r.id] = this.getRuleState(r);
        return o;
      }, {});
    },
    isAutoresponderSelected() {
      return this.selectedAutoresponders.length > 0;
    },
    isRuleSelected() {
      return this.selectedRules.length > 0;
    },
    isSelectedRuleEditable() {
      if (this.selectedRules.length === 1) {
        return !this.selectedRules[0].locked;
      }
      return false;
    },
    canMoveSelectedRuleUp() {
      if (!this.isSelectedRuleEditable) {
        return false;
      }
      const ind = this.localRules.findIndex(r => r.id === this.selectedRules[0].id);
      if (ind === 0) {
        return false;
      }
      if (this.localRules[ind - 1].locked) {
        return false;
      }
      return true;
    },
    canMoveSelectedRuleDown() {
      if (!this.isSelectedRuleEditable) {
        return false;
      }
      const ind = this.localRules.findIndex(r => r.id === this.selectedRules[0].id);
      return (ind < this.localRules.length - 1);
    }
  }
};
</script>

<style lang="scss">
    @import '../assets/bootstrap-style.scss';

    .advancedAutoresponder {
        table {
            .flip-list-move {
                transition: all 0.5s;
            }

            tr.autoresponderForRule {
                font-style: italic;
            }
            tr.disabledRule {
                color: $disabled-autoresponder-rule-colour;
            }
        }
    }
</style>

<style scoped lang="scss">
</style>
